import React, { MediaHTMLAttributes, useEffect, useRef } from "react";
import cn from "classnames";
import { css } from "@emotion/react";
import { toggleableVisibility } from "./styles";
import { VideoResolutions, VideoType } from "@swo/shared";
import useResizeObserver from "use-resize-observer";
import { VideoRef } from "./useSyncedRefProgress";
import { useEqualState } from "../../../hooks";
import { CrossOrigin } from "../../VideoComponent/types";
import { findBestVideoWidth } from "./find-best-video-width";

// +2px is necessary to fulfil parent component
const videoStyles = css`
  &.mode-fill {
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    margin-left: -1px;
    margin-top: -1px;
    background: black;
    object-fit: cover;
  }

  &.mode-contain {
    object-fit: contain;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
`;

export type SharedVideoProps = {
  muted: boolean;
  playsInline: boolean;
  autoPlay: boolean;
  loop: boolean;
  preload: string;
  crossOrigin?: CrossOrigin;
};

export type VideoComponentProps = {
  isFullscreen: boolean;
  video: VideoType["video"];
  sharedVideoProps: SharedVideoProps;
  resolutionPickerStrategy: "middle" | "ceil" | "double-ceil" | number;
  measuredProgress: number;
  measuredPlayingState: "playing" | "paused";
  events: MediaHTMLAttributes<HTMLVideoElement>;
  children?: React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  ref: React.RefObject<HTMLVideoElement | null>;
  responsiveVideo: boolean;
};

export const MeasuredVideoBox: React.FC<React.PropsWithChildren<VideoComponentProps>> = ({ ref, ...props }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { width, height } = useResizeObserver({
    //@ts-expect-error
    ref: containerRef,
  });

  const { wrapperClassName, ...rest } = props;

  return (
    <div
      ref={containerRef}
      className={cn("visual", wrapperClassName)}
      css={[
        toggleableVisibility,
        css`
          line-height: 0;
          overflow: hidden;
        `,
      ]}
    >
      {width && props.responsiveVideo ? (
        <ResponsiveVideoWithSize {...rest} className={cn(props.className)} containerWidth={width} ref={ref} />
      ) : (
        <video className={cn(props.className)} poster={props.video.poster.url} css={videoStyles}>
          {props.children}
        </video>
      )}
    </div>
  );
};

type State = {
  video: VideoResolutions["variants"][0];
  inFullscreen: boolean;
  restoreProgress: "none" | number;
  restorePlayingState: "none" | boolean;
};

const ResponsiveVideoWithSize: React.FC<VideoComponentProps & { containerWidth: number }> = ({ ref, ...props }) => {
  const [selectedVideoState, setSelectedVideoState] = useEqualState<State>(
    getSelectedVideo(props.isFullscreen, props.containerWidth),
  );

  function getSelectedVideo(isInFullscreen: boolean, requestedWidth: number): State {
    const picker = (w: number) => {
      return findBestVideoWidth(w, props.video.derived_formats.mp4.variants, props.resolutionPickerStrategy);
    };

    if (isInFullscreen) {
      return {
        video:
          // Just in case
          typeof window !== "undefined" ? picker(window.innerWidth) : picker(2000),
        inFullscreen: isInFullscreen,
        restoreProgress: "none",
        restorePlayingState: "none",
      };
    } else {
      return {
        video: picker(requestedWidth),
        inFullscreen: isInFullscreen,
        restoreProgress: "none",
        restorePlayingState: "none",
      };
    }
  }

  const poster = props.video.poster.url;

  useEffect(() => {
    const restoreProgress = props.measuredProgress;
    const restorePlayingState = props.measuredPlayingState === "playing";

    const next: State = {
      ...getSelectedVideo(props.isFullscreen, props.containerWidth),
      restoreProgress,
      restorePlayingState,
    };

    setSelectedVideoState(next);
  }, [props.isFullscreen, props.containerWidth]);

  const { crossOrigin, ...sharedVideoProps } = props.sharedVideoProps;

  const url = selectedVideoState.video.video.url;

  return (
    <video
      ref={ref}
      {...sharedVideoProps}
      crossOrigin={crossOrigin}
      {...props.events}
      css={videoStyles}
      className={props.className}
      poster={poster}
      src={url}
      controls={props.isFullscreen}
      onStalled={e => {
        if (props.events.onStalled) {
          props.events.onStalled(e);
        }
      }}
      onWaiting={e => {
        if (props.events.onWaiting) {
          props.events.onWaiting(e);
        }
      }}
      onLoadedMetadata={e => {
        /*
            `onLoadedMetadata` and not `onCanPlay` is used
            because iOS Safari (14.1) doesn't trigger onCanPlay...

            The known problem is creates is that when devices changes orientation
            and video state needs to be restored,
            video neither playing (as onCanPlay is not trigerred),
            nor does it look paused (because we wait until the event is there to play it).

            Looks like onLoadedMetadata is a valid workaround.
           */
        if (props.events.onLoadedMetadata) {
          props.events.onLoadedMetadata(e);
        }

        const current = ref?.current;

        if (!current) {
          if (props.events.onCanPlay) {
            props.events.onCanPlay(e);
          }

          return;
        }

        const { restorePlayingState, restoreProgress } = selectedVideoState;

        const anyAction = typeof restoreProgress === "number" || restorePlayingState === true;

        if (anyAction) {
          if (typeof restoreProgress === "number" && current.currentTime !== restoreProgress) {
            current.currentTime = restoreProgress;
          }

          if (restorePlayingState === true) {
            void current.play();
          }

          setSelectedVideoState({ ...selectedVideoState, restorePlayingState: "none", restoreProgress: "none" });
        }

        if (props.events.onCanPlay) {
          props.events.onCanPlay(e);
        }
      }}
      onError={e => {
        console.log("onError", e);
        if (props.events.onError) {
          props.events.onError(e);
        }
      }}
    >
      {props.children}
    </video>
  );
};
